import React, { useState, useEffect } from 'react';
import { DeskLayout } from '../../components/DeskLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { getProperties, getPropertyReport, seedData, sendBriefing, getEmailLogs, getEPSummary, getBriefingPayload, getFinancialImpact, getTodaysReportStatus } from '../../lib/api';
import { PropertyBadge } from '../../components/PropertyBadge';
import { SIGNAL_LABELS, IMPACT_LABELS, FIX_LABELS } from '../../components/ExperienceProtection';
import { buildTaskTitle } from '../../lib/taskTitles';
import { 
  FileDown, TrendingUp, TrendingDown, Users, Star, MessageSquare, Clock, BarChart3, 
  BellRing, CheckCircle, AlertCircle, Lightbulb, Timer, Mail, ArrowRight, HelpCircle,
  Moon, Wrench, RefreshCw, ExternalLink, Copy, ChevronUp, ChevronDown, Minus,
  AlertTriangle, Shield, Home, Database, Loader2, Info, ClipboardList, Send,
  Bell, UserPlus, Calendar, Coffee, Sunrise, History, Eye, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US')}`;

// Financial Impact section — used inline in the Concierge Briefing web view.
// Mirrors the PDF section so values match across UI / Email / PDF.
// Always sourced from `period='this_week'` so it matches the Today dashboard widget.
const FinancialImpactSection = ({ fi }) => {
  const total = fi?.totalEstimatedSavings || 0;
  const hasData = fi && total > 0;

  if (!hasData) {
    return (
      <div data-testid="financial-impact-section">
        <h4 className="text-sm font-medium text-stone-700 mb-1">Financial Impact</h4>
        <p className="text-xs text-stone-500 mb-3">Estimated value protected from handled tickets, fast resolutions, and recurring issues surfaced by Maris Desk.</p>
        <div className="rounded-xl border border-stone-200 bg-stone-50/40 p-5 text-center">
          <p className="text-[13px] text-stone-500 italic">
            No financial impact calculated yet. Savings appear once tasks are closed.
          </p>
        </div>
      </div>
    );
  }

  const labor = fi.laborEfficiencySavings || 0;
  const preventative = fi.preventativeMaintenanceSavings || 0;
  const reputation = fi.reputationProtectionSavings || 0;
  const hk = fi.housekeepingAmenityTicketsHandled || 0;
  const recur = fi.recurringMaintenanceIssues || 0;
  const fast = fi.fastResolutionsUnder15Minutes || 0;
  return (
    <div data-testid="financial-impact-section">
      <h4 className="text-sm font-medium text-stone-700 mb-1">Financial Impact</h4>
      <p className="text-xs text-stone-500 mb-3">Estimated value protected from handled tickets, fast resolutions, and recurring issues surfaced by Maris Desk.</p>
      <div className="rounded-xl border border-stone-200 bg-gradient-to-br from-stone-50 to-white p-5">
        <div className="flex items-baseline justify-between flex-wrap gap-2 pb-3 border-b border-stone-100">
          <div>
            <p className="text-[11px] tracking-[0.18em] uppercase text-stone-500">Total Estimated Savings</p>
            <p className="font-heading text-[30px] leading-none text-stone-900 mt-1 tabular-nums" data-testid="briefing-financial-total">{fmtMoney(total)}</p>
          </div>
          <span className="text-[11px] text-stone-400">{fi.periodLabel}</span>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-4">
          <div className="space-y-1">
            <p className="text-[11px] uppercase tracking-wide text-stone-500">Labor Efficiency</p>
            <p className="text-lg font-medium text-stone-900 tabular-nums">{fmtMoney(labor)}</p>
            <p className="text-[11.5px] text-stone-500 leading-relaxed">
              Maris Desk helped manage {hk} housekeeping and amenity ticket{hk === 1 ? '' : 's'}. At an average of $15 saved per smooth delivery, this reduced scattered follow-up and manual coordination.
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-[11px] uppercase tracking-wide text-stone-500">Preventative Maintenance</p>
            <p className="text-lg font-medium text-stone-900 tabular-nums">{fmtMoney(preventative)}</p>
            <p className="text-[11.5px] text-stone-500 leading-relaxed">
              Maris Desk surfaced {recur} recurring maintenance issue{recur === 1 ? '' : 's'}. At an estimated $75 protected per early catch, this gives management earlier visibility before issues repeat further.
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-[11px] uppercase tracking-wide text-stone-500">Reputation Protection</p>
            <p className="text-lg font-medium text-stone-900 tabular-nums">{fmtMoney(reputation)}</p>
            <p className="text-[11.5px] text-stone-500 leading-relaxed">
              {fast} guest issue{fast === 1 ? ' was' : 's were'} resolved in under 15 minutes. At an average value of $150 per fast recovery, this helped reduce frustration, repeat complaints, and potential negative review impact.
            </p>
          </div>
        </div>
        <p className="text-[12px] text-stone-600 leading-relaxed mt-4 pt-4 border-t border-stone-100 italic">
          Overall, Maris Desk helped protect an estimated <span className="not-italic font-medium text-stone-900">{fmtMoney(total)}</span>{' '}
          this period by improving follow-through, speeding up guest recovery, and surfacing repeat issues earlier.
        </p>
      </div>
    </div>
  );
};

export const Reports = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [period, setPeriod] = useState('this_month');
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [seedingData, setSeedingData] = useState(false);
  const [briefingDate, setBriefingDate] = useState('today');
  const [includeBriefingInPdf, setIncludeBriefingInPdf] = useState(true);
  
  // Email Briefing State
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailRecipientName, setEmailRecipientName] = useState('');
  const [emailPeriod, setEmailPeriod] = useState('today');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [showEmailHistory, setShowEmailHistory] = useState(false);
  const [epSummary, setEPSummary] = useState(null);
  const [briefingPayload, setBriefingPayload] = useState(null);
  // Always sourced from `period='this_week'` so it mirrors the Today dashboard widget
  const [financialImpact, setFinancialImpact] = useState(null);
  const [reportStatus, setReportStatus] = useState({ status: 'DRAFT', report: null });

  useEffect(() => {
    loadProperties();
    // Fetch today's report close status (DRAFT vs CLOSED)
    getTodaysReportStatus()
      .then((res) => setReportStatus(res.data || { status: 'DRAFT' }))
      .catch(() => setReportStatus({ status: 'DRAFT' }));
  }, []);

  useEffect(() => {
    if (properties.length > 0) {
      loadReport(selectedProperty);
    }
  }, [selectedProperty, period, properties]);

  const loadProperties = async () => {
    try {
      const res = await getProperties();
      setProperties(res.data);
      if (res.data.length > 0) {
        setSelectedProperty(res.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async (propertyId) => {
    if (!propertyId || propertyId === 'all') {
      if (properties.length > 0) {
        propertyId = properties[0].id;
      } else {
        return;
      }
    }
    
    setReportLoading(true);
    try {
      const [reportRes, epRes, bpRes, fiRes] = await Promise.all([
        getPropertyReport(propertyId),
        getEPSummary(propertyId),
        getBriefingPayload(propertyId, 'today'),
        getFinancialImpact(propertyId, 'this_week'),
      ]);
      setReport(reportRes.data);
      setEPSummary(epRes.data);
      setBriefingPayload(bpRes.data);
      setFinancialImpact(fiRes.data);
    } catch (error) {
      console.error('Failed to load report:', error);
      setReport(null);
      setEPSummary(null);
      setBriefingPayload(null);
      setFinancialImpact(null);
    } finally {
      setReportLoading(false);
    }
  };

  const handleSendBriefing = async () => {
    if (!emailRecipient) {
      toast.error('Please enter a recipient email');
      return;
    }
    
    setSendingEmail(true);
    try {
      const res = await sendBriefing({
        propertyId: selectedProperty === 'all' ? null : selectedProperty,
        recipientEmail: emailRecipient,
        recipientName: emailRecipientName,
        period: emailPeriod
      });
      
      setEmailPreview(res.data.preview);
      setShowPreview(true);
      toast.success(`Briefing sent to ${emailRecipient}`, {
        description: 'Demo mode: Email logged for preview'
      });
    } catch (error) {
      toast.error('Failed to send briefing');
      console.error(error);
    } finally {
      setSendingEmail(false);
    }
  };

  const loadEmailHistory = async () => {
    try {
      const res = await getEmailLogs();
      setEmailLogs(res.data);
      setShowEmailHistory(true);
    } catch (error) {
      console.error('Failed to load email history:', error);
    }
  };

  const handleLoadDemoData = async () => {
    setSeedingData(true);
    try {
      await seedData();
      toast.success('Showcase workspace loaded.');
      await loadProperties();
    } catch (error) {
      toast.error('Could not load showcase workspace');
    } finally {
      setSeedingData(false);
    }
  };

  const getPropertyName = () => {
    if (selectedProperty === 'all') return 'All Properties';
    return properties.find(p => p.id === selectedProperty)?.name || 'Unknown';
  };

  // Check if we have meaningful data
  const hasData = report && (
    (report.totalInquiries && report.totalInquiries > 0) ||
    (report.reviewsReceived && report.reviewsReceived > 0) ||
    (report.followupsSent && report.followupsSent > 0)
  );
  
  const hasEnoughData = hasData && (report.totalInquiries >= 5 || report.reviewsReceived >= 3);

  // ============================================================
  // PDF — Concierge Briefing (manager-level, narrative report)
  // ============================================================
  const exportPDF = () => {
    if (!report || !hasData) return;

    const doc = new jsPDF();
    const propertyName = getPropertyName();
    const periodLabel = bp?.header?.periodLabel || new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const periodMap = { today: 'Today', yesterday: 'Yesterday', this_week: 'This Week', this_month: 'This Month', last_month: 'Last Month', this_quarter: 'This Quarter' };
    const periodTitle = periodMap[period] || 'Today';
    const headerColor = [74, 93, 79];      // Maris green
    const subtleColor = [120, 113, 108];   // stone-500
    const inkColor = [41, 37, 36];         // stone-900
    const muted = [168, 162, 158];         // stone-400
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 18;

    // Footer with page numbers — drawn once at end
    let pageCount = 1;

    const ensureSpace = (needed, currentY) => {
      if (currentY + needed > pageH - 22) {
        doc.addPage();
        pageCount += 1;
        return 22;
      }
      return currentY;
    };

    const sectionHeading = (label, y, hint) => {
      y = ensureSpace(18, y);
      doc.setDrawColor(...headerColor);
      doc.setLineWidth(0.6);
      doc.line(margin, y - 4, margin + 6, y - 4);
      doc.setFontSize(13);
      doc.setTextColor(...inkColor);
      doc.setFont(undefined, 'bold');
      doc.text(label, margin, y);
      doc.setFont(undefined, 'normal');
      if (hint) {
        doc.setFontSize(9);
        doc.setTextColor(...subtleColor);
        doc.text(hint, margin, y + 5);
        return y + 11;
      }
      return y + 6;
    };

    const drawTable = (head, body, startY, opts = {}) => {
      autoTable(doc, {
        startY,
        head,
        body,
        theme: 'plain',
        headStyles: {
          fillColor: [248, 247, 244],
          textColor: inkColor,
          fontSize: 9,
          fontStyle: 'bold',
          cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
        },
        bodyStyles: {
          fontSize: 9.5,
          textColor: [55, 53, 47],
          cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
          lineColor: [232, 230, 226],
          lineWidth: 0.1,
        },
        alternateRowStyles: { fillColor: [252, 251, 249] },
        margin: { left: margin, right: margin },
        ...opts,
      });
      return doc.lastAutoTable.finalY + 8;
    };

    const writeParagraph = (text, y, opts = {}) => {
      const fontSize = opts.fontSize || 10.5;
      const color = opts.color || inkColor;
      doc.setFontSize(fontSize);
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(text, pageW - margin * 2);
      y = ensureSpace(lines.length * (fontSize * 0.4) + 4, y);
      doc.text(lines, margin, y);
      return y + lines.length * (fontSize * 0.4) + 3;
    };

    const stripNonAscii = (s) => (s || '').replace(/[^\x00-\x7F]/g, '');
    const fmtTime = (iso) => {
      if (!iso) return '—';
      try {
        return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      } catch { return '—'; }
    };
    const OWNER_LABEL_MAP = {
      front_desk: 'Front Desk', housekeeping: 'Housekeeping', maintenance: 'Maintenance',
      transport: 'Transport', concierge: 'Concierge', reservations: 'Reservations',
      manager_on_duty: 'Manager on Duty',
    };
    const prettyOwner = (raw) => raw ? (OWNER_LABEL_MAP[String(raw).toLowerCase()] || String(raw)) : '';
    const ownerLabel = (t) => stripNonAscii(prettyOwner(t.assigned_to_user_id) || t.assignedRole || 'Unassigned');
    const ownerWithRoute = (t) => {
      const owner = ownerLabel(t);
      const route = t.route_to ? prettyOwner(t.route_to) : '';
      return route && route.toLowerCase() !== owner.toLowerCase() ? `${owner} → ${route}` : owner;
    };
    const guestLabel = (t) => stripNonAscii(t.guest || t.guest_name_text || 'Guest');
    const titleOf = (t) => stripNonAscii(buildTaskTitle({ category: t.category, summary: t.summary, guest_name: t.guest || t.guest_name_text }));
    const priorityLabel = (p) => ({ HANDLE_NOW: 'Handle Now', DUE_TODAY: 'Due Today', WAITING_ON: 'Waiting', SCHEDULED: 'Scheduled' }[p] || (p || '').replace(/_/g,' '));
    const reasonLabel = (r) => (r || 'Not recorded').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    // ---- 1. HEADER --------------------------------------------------------
    doc.setFontSize(11);
    doc.setTextColor(...headerColor);
    doc.setFont(undefined, 'bold');
    doc.text('MARIS DESK', margin, 20);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(22);
    doc.setTextColor(...inkColor);
    doc.text('Concierge Briefing', margin, 32);
    doc.setFontSize(13);
    doc.setTextColor(...inkColor);
    doc.text(propertyName, margin, 42);
    doc.setFontSize(10);
    doc.setTextColor(...subtleColor);
    doc.text(`${periodTitle}  ·  ${periodLabel}`, margin, 49);
    doc.setDrawColor(...headerColor);
    doc.setLineWidth(0.4);
    doc.line(margin, 53, pageW - margin, 53);

    let y = 63;

    // ---- 2. TODAY'S BRIEF / MANAGER NOTE ----------------------------------
    const totalItems = (report.totalInquiries || 0);
    const openItems = bp?.morningBrief?.open || 0;
    const handleNowItems = bp?.morningBrief?.handleNow || 0;
    const reviewsWaiting = (report.reviewsReceived || 0) - (report.reviewsReceived - (report.pendingReviews || 0));
    const repeatCats = (bp?.experienceProtection?.repeatsToFix || []).slice(0, 3).map(r => (r.category || '').replace(/_/g,' ').toLowerCase()).filter(Boolean);
    const attentionWords = [];
    if (handleNowItems > 0) attentionWords.push('open urgent items');
    if (openItems > 0) attentionWords.push('unresolved guest requests');
    if ((report.pendingReviews || 0) > 0) attentionWords.push('pending review responses');
    if (repeatCats.length > 0) attentionWords.push(`repeat questions around ${repeatCats.join(', ')}`);
    const attentionPhrase = attentionWords.length > 0
      ? attentionWords.slice(0, 3).join(', ')
      : 'no major pressure points right now';
    const managerNote = `${propertyName} handled ${totalItems} guest item${totalItems === 1 ? '' : 's'} this period. The main attention areas are ${attentionPhrase}.`;

    y = sectionHeading("Today's Brief", y, 'A short read for the manager on duty.');
    y = writeParagraph(managerNote, y);
    y += 4;

    // ---- 3. GUEST DESK SNAPSHOT -------------------------------------------
    y = sectionHeading('Guest Desk Snapshot', y, 'Where the day stands at a glance.');
    const closedCount = bp?.morningBrief?.closed || 0;
    const followupsSent = report.followupsSent || 0;
    const stillOpen = openItems;
    const needsAttention = handleNowItems + ((bp?.morningBrief?.unassigned) || 0);
    const newRequests = totalItems;
    const reviewsPending = report.pendingReviews || 0;
    const snapshotRows = [
      ['New guest requests', String(newRequests)],
      ['Closed out',         String(closedCount)],
      ['Still open',         String(stillOpen)],
      ['Needs attention',    String(needsAttention)],
      ['Reviews waiting',    String(reviewsPending)],
      ['Follow-ups sent',    String(followupsSent)],
    ];
    y = drawTable([['Snapshot', 'Count']], snapshotRows, y, {
      columnStyles: { 0: { cellWidth: 70 }, 1: { halign: 'right' } },
    });

    // ---- 3b. FINANCIAL IMPACT --------------------------------------------
    // Source: dedicated `financialImpact` state (period='this_week'), so the PDF
    // matches the Today dashboard widget exactly — not the briefing's date scope.
    const fi = financialImpact;
    const fiTotalPdf = fi?.totalEstimatedSavings || 0;
    if (fi && fiTotalPdf > 0) {
      y = sectionHeading('Financial Impact', y, 'Estimated value protected from handled tickets, fast resolutions, and recurring issues surfaced by Maris Desk.');
      // Total headline
      doc.setFontSize(10);
      doc.setTextColor(...subtleColor);
      doc.text('Total Estimated Savings', margin, y);
      doc.setFontSize(20);
      doc.setTextColor(...inkColor);
      doc.text(fmtMoney(fi.totalEstimatedSavings || 0), margin, y + 9);
      y += 16;
      // Breakdown table — executive copy spec'd by V1.
      const hkN = fi.housekeepingAmenityTicketsHandled || 0;
      const recN = fi.recurringMaintenanceIssues || 0;
      const fastN = fi.fastResolutionsUnder15Minutes || 0;
      const finRows = [
        [
          'Labor Efficiency',
          fmtMoney(fi.laborEfficiencySavings || 0),
          `Maris Desk helped manage ${hkN} housekeeping and amenity ticket${hkN === 1 ? '' : 's'}. At an average of $15 saved per smooth delivery, this reduced scattered follow-up and manual coordination.`,
        ],
        [
          'Preventative Maintenance',
          fmtMoney(fi.preventativeMaintenanceSavings || 0),
          `Maris Desk surfaced ${recN} recurring maintenance issue${recN === 1 ? '' : 's'}. At an estimated $75 protected per early catch, this gives management earlier visibility before issues repeat further.`,
        ],
        [
          'Reputation Protection',
          fmtMoney(fi.reputationProtectionSavings || 0),
          `${fastN} guest issue${fastN === 1 ? ' was' : 's were'} resolved in under 15 minutes. At an average value of $150 per fast recovery, this helped reduce frustration, repeat complaints, and potential negative review impact.`,
        ],
      ];
      y = drawTable(
        [['Category', 'Estimated', 'How it adds up']],
        finRows,
        y,
        { columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 26, halign: 'right' }, 2: { cellWidth: 102 } } },
      );
      y = writeParagraph(
        `Overall, Maris Desk helped protect an estimated ${fmtMoney(fi.totalEstimatedSavings || 0)} ` +
        `this period by improving follow-through, speeding up guest recovery, and surfacing repeat issues earlier.`,
        y,
        { color: inkColor, fontSize: 10 },
      );
      y += 2;
    } else {
      y = sectionHeading('Financial Impact', y, 'Estimated value protected from handled tickets, fast resolutions, and recurring issues surfaced by Maris Desk.');
      y = writeParagraph(
        'No financial impact calculated yet. Savings appear once tasks are closed.',
        y,
        { color: subtleColor, fontSize: 10 },
      );
      y += 2;
    }

    // ---- 4. WHAT NEEDS ATTENTION ------------------------------------------
    y = sectionHeading('What Needs Attention', y, 'Handle Now, Due Today, Waiting On, and Carry-Over — open items only.');
    const attentionItems = [
      ...((bp?.sections?.handleNow || [])),
      ...((bp?.sections?.dueToday || [])),
      ...((bp?.sections?.waitingOn || [])),
      ...((bp?.sections?.carryOver || [])),
    ];
    if (attentionItems.length === 0) {
      y = writeParagraph('No open items needing attention right now.', y, { color: subtleColor, fontSize: 10 });
    } else {
      const rows = attentionItems.slice(0, 15).map(t => {
        const nextStep = stripNonAscii(t.next_touch_at ? `Next touch ${fmtTime(t.next_touch_at)}` : (t.dueAt ? `Due ${fmtTime(t.dueAt)}` : 'Take next action'));
        const due = stripNonAscii(t.dueAt ? fmtTime(t.dueAt) : (t.next_touch_at ? fmtTime(t.next_touch_at) : '—'));
        return [
          priorityLabel(t.priority),
          guestLabel(t),
          titleOf(t).slice(0, 60),
          nextStep,
          ownerWithRoute(t),
          due,
        ];
      });
      y = drawTable(
        [['Priority', 'Guest / Area', 'Issue', 'Next Step', 'Owner', 'Due']],
        rows,
        y,
        { columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 28 }, 2: { cellWidth: 50 }, 3: { cellWidth: 35 }, 4: { cellWidth: 22 }, 5: { cellWidth: 18 } } },
      );
    }

    // ---- 5. WHAT WE HANDLED -----------------------------------------------
    y = sectionHeading('What We Handled', y, 'Closed items for this period.');
    const handledItems = bp?.sections?.whatWeHandled || [];
    if (handledItems.length === 0) {
      y = writeParagraph('No items closed yet for this period.', y, { color: subtleColor, fontSize: 10 });
    } else {
      const rows = handledItems.slice(0, 15).map(t => [
        stripNonAscii(t.closed_at ? fmtTime(t.closed_at) : '—'),
        guestLabel(t),
        titleOf(t).slice(0, 60),
        ownerLabel(t),
        'Resolved',
      ]);
      y = drawTable(
        [['Closed Time', 'Guest / Area', 'Request', 'Handled By', 'Outcome']],
        rows,
        y,
        { columnStyles: { 0: { cellWidth: 26 }, 1: { cellWidth: 32 }, 2: { cellWidth: 65 }, 3: { cellWidth: 30 }, 4: { cellWidth: 22 } } },
      );
    }

    // ---- 6. WAITING / CARRY-OVER ------------------------------------------
    y = sectionHeading('Waiting / Carry-Over', y, 'Unfinished items still in play.');
    const waitItems = [...((bp?.sections?.waitingOn || [])), ...((bp?.sections?.carryOver || []))];
    if (waitItems.length === 0) {
      y = writeParagraph('Nothing waiting — clean board.', y, { color: subtleColor, fontSize: 10 });
    } else {
      const rows = waitItems.slice(0, 12).map(t => [
        guestLabel(t),
        reasonLabel(t.carry_over_reason || t.waiting_on),
        stripNonAscii(t.next_touch_at ? fmtTime(t.next_touch_at) : 'Not set'),
        ownerWithRoute(t),
      ]);
      y = drawTable(
        [['Guest / Area', 'Reason', 'Next Touch', 'Owner']],
        rows,
        y,
        { columnStyles: { 0: { cellWidth: 45 }, 1: { cellWidth: 60 }, 2: { cellWidth: 35 }, 3: { cellWidth: 35 } } },
      );
    }

    // ---- 7. OWNER DECISIONS NEEDED ----------------------------------------
    y = sectionHeading('Owner Decisions Needed', y, 'Items waiting on a manager call.');
    const approvals = bp?.sections?.ownerApprovals || [];
    if (approvals.length === 0) {
      y = writeParagraph('No owner decisions needed today.', y, { color: subtleColor, fontSize: 10 });
    } else {
      const rows = approvals.slice(0, 8).map(t => [
        stripNonAscii(t.owner_decision_summary || titleOf(t)).slice(0, 60),
        guestLabel(t),
        stripNonAscii((t.impact_area || 'Guest experience').replace(/_/g, ' ')),
        stripNonAscii(t.dueAt ? fmtTime(t.dueAt) : 'Today'),
      ]);
      y = drawTable(
        [['Decision Needed', 'Guest / Area', 'Why It Matters', 'Needed By']],
        rows,
        y,
        { columnStyles: { 0: { cellWidth: 65 }, 1: { cellWidth: 30 }, 2: { cellWidth: 50 }, 3: { cellWidth: 30 } } },
      );
    }

    // ---- 8. EXPERIENCE PROTECTION -----------------------------------------
    y = sectionHeading('Experience Protection', y, 'Patterns to fix so the same friction does not return.');
    const repeats = bp?.experienceProtection?.repeatsToFix || [];
    if (repeats.length === 0) {
      y = writeParagraph('No repeat patterns detected this period.', y, { color: subtleColor, fontSize: 10 });
    } else {
      const whyByCat = {
        TRANSPORT:    'Guests need clarity before arrival',
        BILLING:      'May slow booking decisions',
        HOUSEKEEPING: 'Could become repeat service friction',
        ACCESS:       'Friction at arrival hurts first impression',
        ARRIVAL:      'Sets the tone for the entire stay',
        MAINTENANCE:  'Comfort issues escalate fast',
        BOOKING:      'Lost bookings cost real revenue',
        INQUIRY:      'Slow answers lose trust',
        CONCIERGE:    'Affects perceived service quality',
      };
      const fixByCat = {
        TRANSPORT:    'Add airport transfer details to GuestHub',
        BILLING:      'Add payment FAQ to property guide',
        HOUSEKEEPING: 'Add extra towel / amenity request to GuestHub',
        ACCESS:       'Add lockbox photos to pre-arrival message',
        ARRIVAL:      'Add check-in timing reminder',
        MAINTENANCE:  'Document fix routes for top 3 issues',
        BOOKING:      'Add saved reply with pricing + availability',
        INQUIRY:      'Build a quick-reply for the top question',
        CONCIERGE:    'Add a vendor short-list to property notes',
      };
      const rows = repeats.slice(0, 6).map(r => {
        const cat = (r.category || 'OTHER').toUpperCase();
        const label = cat.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
        return [
          `${label} questions`,
          `${r.count}x`,
          whyByCat[cat] || 'Reduces repeat service load',
          fixByCat[cat] || (r.suggested_fix_route ? r.suggested_fix_route.replace(/_/g,' ') : 'Add to GuestHub / quick replies'),
        ];
      });
      y = drawTable(
        [['Pattern', 'Seen', 'Why It Matters', 'Recommended Fix']],
        rows,
        y,
        { columnStyles: { 0: { cellWidth: 45 }, 1: { cellWidth: 18, halign: 'center' }, 2: { cellWidth: 55 }, 3: { cellWidth: 60 } } },
      );
    }

    // ---- 9. RECOMMENDED NEXT MOVES ----------------------------------------
    y = sectionHeading('Recommended Next Moves', y, '3–5 action points for the day.');
    const moves = [];
    if ((bp?.morningBrief?.unassigned || 0) > 0) moves.push('Assign all unowned urgent items.');
    if ((report.pendingReviews || 0) > 0) moves.push('Respond to pending guest reviews.');
    if (handleNowItems > 0) moves.push('Close out remaining Handle Now items before end of shift.');
    if ((bp?.sections?.carryOver || []).length > 0) moves.push('Review and reschedule carry-over items with a next touch time.');
    if (repeatCats.length > 0) moves.push(`Add repeat questions (${repeatCats.slice(0, 2).join(', ')}) to GuestHub / quick replies.`);
    if (approvals.length > 0) moves.push('Get owner sign-off on pending decisions.');
    if (moves.length === 0) moves.push('Hold the line — clean board, keep response times tight.');

    doc.setFontSize(10.5);
    doc.setTextColor(...inkColor);
    moves.slice(0, 5).forEach((line) => {
      const lines = doc.splitTextToSize(`•  ${stripNonAscii(line)}`, pageW - margin * 2 - 4);
      y = ensureSpace(lines.length * 5 + 1, y);
      doc.text(lines, margin + 2, y);
      y += lines.length * 5 + 1;
    });

    // ---- FOOTER on every page --------------------------------------------
    const total = doc.internal.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      doc.setFontSize(8.5);
      doc.setTextColor(...muted);
      doc.text(`Generated by Maris Desk  ·  ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`, margin, pageH - 10);
      doc.text(`Page ${i} of ${total}`, pageW - margin, pageH - 10, { align: 'right' });
    }

    const fileSafeProp = propertyName.replace(/\s+/g, '-').toLowerCase();
    const fileSafeDate = (bp?.header?.periodLabel || '').replace(/[,\s]+/g, '-').toLowerCase() || new Date().toISOString().slice(0, 10);
    doc.save(`concierge-briefing-${fileSafeProp}-${fileSafeDate}.pdf`);
    toast.success('Concierge Briefing exported.');
  };

  // Stat Card Component
  const StatCard = ({ title, value, microcopy, icon: Icon, color, trend, trendLabel }) => (
    <Card className="border-stone-200 hover:border-stone-300 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-stone-500">{title}</p>
            <p className="text-2xl font-heading font-medium mt-1">{value}</p>
            {trendLabel && (
              <p className="text-xs text-stone-500 mt-1 flex items-center gap-1">
                {trend === 'up' && <ChevronUp className="h-3 w-3 text-green-500" />}
                {trend === 'down' && <ChevronDown className="h-3 w-3 text-red-500" />}
                {trend === 'same' && <Minus className="h-3 w-3 text-stone-400" />}
                {trendLabel}
              </p>
            )}
            {microcopy && <p className="text-xs text-stone-400 mt-1">{microcopy}</p>}
          </div>
          <div className={`p-2.5 rounded-full ${color}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Enhanced Empty State Component
  const EmptyState = ({ icon: Icon, title, description, hint, children }) => (
    <div className="text-center py-8 px-4">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-stone-100 mb-4">
        <Icon className="h-6 w-6 text-stone-400" />
      </div>
      <p className="text-stone-700 font-medium">{title}</p>
      <p className="text-sm text-stone-500 mt-1 max-w-md mx-auto">{description}</p>
      {hint && <p className="text-xs text-stone-400 mt-2">{hint}</p>}
      {children}
    </div>
  );

  // Trend Badge Component
  const TrendBadge = ({ trend }) => {
    if (!trend) return null;
    const isUp = trend > 0;
    const isDown = trend < 0;
    return (
      <Badge variant="outline" className={`text-xs ${isUp ? 'text-green-600 border-green-200' : isDown ? 'text-red-600 border-red-200' : 'text-stone-500'}`}>
        {isUp ? <ChevronUp className="h-3 w-3" /> : isDown ? <ChevronDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
        {Math.abs(trend)}%
      </Badge>
    );
  };

  // Page-level empty state (no data at all)
  const PageEmptyState = () => (
    <Card className="border-stone-200">
      <CardContent className="py-16 px-8">
        <div className="text-center max-w-lg mx-auto">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-stone-100 mb-6">
            <BarChart3 className="h-8 w-8 text-stone-400" />
          </div>
          <h2 className="text-xl font-heading font-medium text-stone-800">Nothing to report yet</h2>
          <p className="text-stone-500 mt-3 leading-relaxed">
            Once messages start coming in, this page will show what guests asked for, what took time, and what needs tightening up.
          </p>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <DeskLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <Skeleton key={i} className="h-28" />
            ))}
          </div>
        </div>
      </DeskLayout>
    );
  }

  // Helper to format response time humanly
  const formatResponseTime = (minutes) => {
    if (!minutes || minutes <= 0) return '—';
    // Cap at reasonable maximum (24 hours = 1440 min)
    if (minutes > 1440) return '> 24h';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours >= 24) return '> 24h';
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Calculate derived values
  const openCount = report ? Math.max(0, (report.totalInquiries || 0) - (report.booked || 0) - (report.lost || 0)) : 0;
  const convRate = report && report.totalInquiries > 0 ? ((report.booked || 0) / report.totalInquiries * 100).toFixed(1) : '0';
  const responseRate = report && report.reviewsReceived > 0 
    ? Math.round(((report.reviewsReceived - (report.pendingReviews || 0)) / report.reviewsReceived) * 100) 
    : 0;

  // Sample Briefing Data (would come from Daily Run tasks in production)
  // Use single briefing payload for all briefing sections
  const bp = briefingPayload;
  const briefingData = bp ? {
    completed: bp.morningBrief?.closed || 0,
    stillOpen: bp.morningBrief?.open || 0,
    carryOver: bp.sections?.carryOver?.length || 0,
    escalations: bp.morningBrief?.handleNow || 0,
    afterHours: 0,
    whatWeHandled: (bp.sections?.whatWeHandled || []).map(t => ({
      priority: t.priority?.replace(/_/g, ' '),
      propertyId: properties.find(p => p.name === bp.header?.propertyName)?.id || properties[0]?.id,
      category: t.category?.replace(/_/g, ' '),
      summary: buildTaskTitle({ category: t.category, summary: t.summary, guest_name: t.guest_name_text }),
      closed: t.closed_at ? new Date(t.closed_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '',
      owner: t.assigned_to_user_id || '',
    })),
    stillWaiting: (bp.sections?.waitingOn || []).map(t => ({
      priority: t.priority?.replace(/_/g, ' '),
      propertyId: properties[0]?.id,
      waitingOn: t.waiting_on?.replace(/_/g, ' ') || 'Not recorded yet',
      summary: buildTaskTitle({ category: t.category, summary: t.summary, guest_name: t.guest_name_text }),
      nextTouch: t.next_touch_at ? new Date(t.next_touch_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'Not set',
      owner: t.assigned_to_user_id || null,
      status: 'Waiting',
    })),
    carryOverNotes: (bp.sections?.carryOver || []).map(t => ({
      propertyId: properties[0]?.id,
      summary: buildTaskTitle({ category: t.category, summary: t.summary, guest_name: t.guest_name_text }),
      reason: t.carry_over_reason?.replace(/_/g, ' ') || 'Not recorded yet',
      nextTouch: t.next_touch_at ? new Date(t.next_touch_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'Not set',
      owner: t.assigned_to_user_id || null,
    })),
    pressurePoints: (bp.experienceProtection?.repeatsToFix || []).map(r => ({
      category: r.category?.replace(/_/g, ' '),
      count: r.count,
      properties: 1,
      fix: r.suggested_fix_route?.replace(/_/g, ' ') || '',
    })),
    tomorrowHeadStart: [],
  } : null;

  // Placeholder breakdown for Most Common Requests until live data populates
  const commonRequests = hasData ? [
    { category: 'WiFi / Connectivity', count: 12, pct: 24, trend: 5 },
    { category: 'Late Checkout', count: 8, pct: 16, trend: -2 },
    { category: 'Airport Transfer', count: 7, pct: 14, trend: 3 },
    { category: 'Early Check-in', count: 5, pct: 10, trend: 0 },
    { category: 'Restaurant Reservations', count: 4, pct: 8, trend: 1 }
  ] : [];

  // Sample recurring issues
  const recurringIssues = hasData ? [
    { issue: 'No hot water', count: 3, properties: 'SeaGlass Villa', lastSeen: '2 days ago', status: 'Open' },
    { issue: 'WiFi slow in bedroom', count: 2, properties: 'Palm & Linen', lastSeen: '5 days ago', status: 'Recurring' }
  ] : [];

  // Sample fix suggestions
  const fixSuggestions = hasData ? [
    { title: 'Reduce WiFi questions', why: '12 requests this month', action: 'Add WiFi info to GuestHub + quick reply template' },
    { title: 'Speed up late checkout responses', why: 'Avg 45 min response time', action: 'Create saved reply with pricing + availability check' },
    { title: 'Review reply backlog', why: '3 reviews pending > 48 hours', action: 'Prioritize oldest reviews first' }
  ] : [];

  return (
    <DeskLayout>
      <div className="space-y-6 animate-fade-in" data-testid="reports-page">
        {/* Period close status banner (only on today's period) */}
        {period === 'today' && reportStatus.status !== 'CLOSED' && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="report-draft-banner">
            <Clock className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-900">Today's report is still live — period closes at End Shift.</p>
              <p className="text-xs text-amber-700 mt-0.5">
                You can preview the brief here, but PDF export and email send are locked until the shift is closed. Past briefings are available on Daily Run.
              </p>
            </div>
          </div>
        )}
        {period === 'today' && reportStatus.status === 'CLOSED' && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 flex items-start gap-3" data-testid="report-closed-banner">
            <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-900">Today's report is closed and locked.</p>
              <p className="text-xs text-green-700 mt-0.5">
                Safe to export and share — this is the finalized concierge briefing for today.
              </p>
            </div>
          </div>
        )}

        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900">Reports</h1>
            <p className="text-sm text-stone-500 mt-1">See what's happening, what's slipping, and what to fix next.</p>
            <p className="text-xs text-stone-400 mt-0.5">
              Reports update as messages come in and requests get marked done.
              <span className="italic ml-1">If the week felt loud, it should show here.</span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Property Select */}
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-48" data-testid="report-property-select">
                <SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Period Select */}
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="this_week">This Week</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="this_quarter">This Quarter</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Compare Toggle */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2">
                    <Switch 
                      id="compare" 
                      checked={compareEnabled} 
                      onCheckedChange={setCompareEnabled}
                      disabled={!hasData}
                    />
                    <Label htmlFor="compare" className={`text-sm ${!hasData ? 'text-stone-400' : 'text-stone-500'}`}>
                      Compare
                    </Label>
                  </div>
                </TooltipTrigger>
                {!hasData && (
                  <TooltipContent>
                    <p className="text-xs">Compare turns on once there's a previous period to measure against.</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            
            {/* Export Button — Only enabled once today's report is closed (End Shift) */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button 
                      onClick={exportPDF}
                      disabled={!hasData || (period === 'today' && reportStatus.status !== 'CLOSED')}
                      className="bg-[#3F617E] hover:bg-[#345470] rounded-full disabled:opacity-50"
                      data-testid="export-pdf-button"
                    >
                      <FileDown className="h-4 w-4 mr-2" /> Export PDF
                    </Button>
                  </span>
                </TooltipTrigger>
                {(!hasData || (period === 'today' && reportStatus.status !== 'CLOSED')) && (
                  <TooltipContent>
                    <p className="text-xs">
                      {period === 'today' && reportStatus.status !== 'CLOSED'
                        ? 'Today\u2019s report locks at End Shift. Available right after.'
                        : 'Export is available once there\u2019s something to summarize.'}
                    </p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            
            {/* Send Email Briefing Button — Only enabled once today's report is closed */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button 
                      onClick={() => setShowEmailModal(true)}
                      disabled={!hasData || (period === 'today' && reportStatus.status !== 'CLOSED')}
                      variant="outline"
                      className="rounded-full disabled:opacity-50"
                      data-testid="send-briefing-button"
                    >
                      <Mail className="h-4 w-4 mr-2" /> Send Briefing
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Send the concierge briefing via email to managers.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        
        {/* Filter Tip */}
        {selectedProperty !== 'all' && properties.length > 1 && (
          <p className="text-xs text-stone-400 -mt-2">
            Want patterns? Switch to "All Properties".
          </p>
        )}
        
        {/* Updated timestamp */}
        {hasData && (
          <div className="flex justify-end -mt-2">
            <p className="text-xs text-stone-400">Updated just now</p>
          </div>
        )}

        {reportLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <Skeleton key={i} className="h-28" />
            ))}
          </div>
        ) : !hasData ? (
          <PageEmptyState />
        ) : (
          <>
            {/* 1) Executive Snapshot */}
            <div>
              <h2 className="text-lg font-heading font-medium text-stone-800 mb-3">Executive Snapshot</h2>
              {report.totalInquiries > 0 || report.reviewsReceived > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard
                    title="Total Inquiries"
                    value={report.totalInquiries || 0}
                    microcopy="New guest messages started this period."
                    icon={MessageSquare}
                    color="bg-blue-100 text-blue-600"
                  />
                  <StatCard
                    title="Booked"
                    value={report.booked || 0}
                    microcopy="Confirmed bookings from inquiries."
                    trendLabel={report.totalInquiries > 0 ? `${convRate}% conversion` : null}
                    icon={TrendingUp}
                    color="bg-green-100 text-green-600"
                  />
                  <StatCard
                    title="Lost"
                    value={report.lost || 0}
                    microcopy="Inquiries that didn't convert."
                    icon={TrendingDown}
                    color="bg-red-100 text-red-600"
                  />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <StatCard
                            title="Open"
                            value={openCount}
                            microcopy="Often needs a quick follow-up."
                            icon={Clock}
                            color="bg-amber-100 text-amber-600"
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Open = still waiting on an outcome (not booked, not lost).</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <StatCard
                    title="Reviews Received"
                    value={report.reviewsReceived || 0}
                    microcopy="New reviews posted this period."
                    icon={Star}
                    color="bg-amber-100 text-amber-600"
                  />
                  <StatCard
                    title="Average Rating"
                    value={report.avgRating ? report.avgRating.toFixed(1) : '—'}
                    microcopy={report.avgRating ? "Across new reviews this period." : "No reviews logged yet."}
                    icon={Star}
                    color="bg-yellow-100 text-yellow-600"
                  />
                  <StatCard
                    title="Review Replies"
                    value={report.reviewsReceived > 0 && responseRate > 0 ? `${responseRate}%` : '—'}
                    microcopy={responseRate > 0 ? "Replies sent this period." : "No replies sent yet."}
                    icon={CheckCircle}
                    color="bg-emerald-100 text-emerald-600"
                  />
                  <StatCard
                    title="Follow-ups Sent"
                    value={report.followupsSent > 0 ? report.followupsSent : '—'}
                    microcopy={report.followupsSent > 0 ? "Messages sent to keep things moving." : "Not enough history yet."}
                    icon={RefreshCw}
                    color="bg-indigo-100 text-indigo-600"
                  />
                </div>
              ) : (
                <Card className="border-stone-200">
                  <CardContent className="p-0">
                    <EmptyState 
                      icon={BarChart3}
                      title="No snapshot yet"
                      description="We'll build your snapshot as soon as the first conversations land—bookings, open inquiries, follow-ups, and reviews included."
                      hint={isAdmin ? "Tip: Load the Showcase workspace to preview the full layout." : undefined}
                    />
                  </CardContent>
                </Card>
              )}
            </div>

            {/* CONCIERGE BRIEFING - Daily Run Recap */}
            <Card className="border-stone-200 border-2 border-[#3F617E]/30">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="h-5 w-5 text-[#3F617E]" />
                    <CardTitle className="font-heading text-lg">Concierge Briefing</CardTitle>
                  </div>
                  <div className="flex items-center gap-3">
                    <Select value={briefingDate} onValueChange={setBriefingDate}>
                      <SelectTrigger className="w-28 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="today">Today</SelectItem>
                        <SelectItem value="yesterday">Yesterday</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-1.5">
                      <Switch 
                        id="include-briefing" 
                        checked={includeBriefingInPdf} 
                        onCheckedChange={setIncludeBriefingInPdf}
                        className="h-4 w-7"
                      />
                      <Label htmlFor="include-briefing" className="text-xs text-stone-500">Include in PDF</Label>
                    </div>
                  </div>
                </div>
                <CardDescription>A quick read of what we handled, what's still open, and what needs attention next.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {briefingData ? (
                  <>
                    {/* Shift Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <p className="text-2xl font-heading font-medium text-green-700">{briefingData.completed}</p>
                        <p className="text-xs font-medium text-green-600">Completed</p>
                        <p className="text-[10px] text-green-500 mt-0.5">Tasks closed this shift</p>
                      </div>
                      <div className="p-3 bg-amber-50 rounded-lg text-center">
                        <p className="text-2xl font-heading font-medium text-amber-700">{briefingData.stillOpen}</p>
                        <p className="text-xs font-medium text-amber-600">Still Open</p>
                        <p className="text-[10px] text-amber-500 mt-0.5">Needs attention</p>
                      </div>
                      <div className="p-3 bg-purple-50 rounded-lg text-center">
                        <p className="text-2xl font-heading font-medium text-purple-700">{briefingData.carryOver}</p>
                        <p className="text-xs font-medium text-purple-600">Carry-Over</p>
                        <p className="text-[10px] text-purple-500 mt-0.5">Moved with next step</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg text-center">
                        <p className="text-2xl font-heading font-medium text-red-700">{briefingData.escalations}</p>
                        <p className="text-xs font-medium text-red-600">Escalations</p>
                        <p className="text-[10px] text-red-500 mt-0.5">Routed to team/vendor</p>
                      </div>
                      <div className="p-3 bg-indigo-50 rounded-lg text-center">
                        <p className="text-2xl font-heading font-medium text-indigo-700">{briefingData.afterHours}</p>
                        <p className="text-xs font-medium text-indigo-600">After-Hours</p>
                        <p className="text-[10px] text-indigo-500 mt-0.5">Outside coverage</p>
                      </div>
                    </div>

                    {/* Financial Impact — owner/GM value summary.
                        Sourced from `period='this_week'` so it always matches the Today dashboard widget. */}
                    <FinancialImpactSection fi={financialImpact} />

                    {/* Experience Protection Section */}
                    <div data-testid="experience-protection-section">
                      <h4 className="text-sm font-medium text-stone-700 mb-1">Experience Protection</h4>
                      <p className="text-xs text-stone-500 mb-3">The quiet friction + the simple fixes.</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Watch first */}
                        <div className="p-4 bg-red-50/50 rounded-lg border border-red-100">
                          <h5 className="text-xs font-semibold text-stone-800 mb-1">Watch first</h5>
                          <p className="text-[10px] text-stone-500 mb-2">These protect access, comfort, trust, or safety.</p>
                          {epSummary?.watch_first?.length > 0 ? (
                            <ul className="space-y-1.5">
                              {epSummary.watch_first.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <AlertTriangle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                                  <span className="text-xs text-stone-700">
                                    <span className="font-medium">{IMPACT_LABELS[item.impact_area] || item.impact_area}:</span> {item.summary?.slice(0, 55)}{item.summary?.length > 55 ? '...' : ''}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-stone-400 italic">No active risks right now.</p>
                          )}
                        </div>

                        {/* Repeats to fix */}
                        <div className="p-4 bg-amber-50/50 rounded-lg border border-amber-100">
                          <h5 className="text-xs font-semibold text-stone-800 mb-1">Repeats to fix</h5>
                          <p className="text-[10px] text-stone-500 mb-2">High-frequency requests that can be prevented.</p>
                          {epSummary?.repeats_to_fix?.length > 0 ? (
                            <ul className="space-y-1.5">
                              {epSummary.repeats_to_fix.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <RefreshCw className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />
                                  <span className="text-xs text-stone-700">
                                    <span className="font-medium">{item.category?.replace(/_/g, ' ')}</span> ({item.count}x) — {FIX_LABELS[item.suggested_fix_route] || item.suggested_fix_route}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-stone-400 italic">No recurring patterns detected yet.</p>
                          )}
                        </div>

                        {/* Quiet delight */}
                        <div className="p-4 bg-emerald-50/50 rounded-lg border border-emerald-100">
                          <h5 className="text-xs font-semibold text-stone-800 mb-1">Quiet delight</h5>
                          <p className="text-[10px] text-stone-500 mb-2">Small touches guests remember.</p>
                          {epSummary?.quiet_delight?.length > 0 ? (
                            <ul className="space-y-1.5">
                              {epSummary.quiet_delight.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <Star className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                                  <span className="text-xs text-stone-700">{item.summary?.slice(0, 60)}{item.summary?.length > 60 ? '...' : ''}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-stone-400 italic">No delight moments queued yet.</p>
                          )}
                        </div>

                        {/* Manager approvals */}
                        <div className="p-4 bg-indigo-50/50 rounded-lg border border-indigo-100">
                          <h5 className="text-xs font-semibold text-stone-800 mb-1">
                            Manager approvals ({epSummary?.owner_approvals_count || 0})
                          </h5>
                          <p className="text-[10px] text-stone-500 mb-2">Decisions that need a sign-off — GM, Front Desk Manager, or Manager on Duty.</p>
                          {epSummary?.owner_approvals?.length > 0 ? (
                            <ul className="space-y-1.5">
                              {epSummary.owner_approvals.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <Bell className="h-3 w-3 text-indigo-500 mt-0.5 flex-shrink-0" />
                                  <span className="text-xs text-stone-700">
                                    {item.owner_decision_summary || item.summary?.slice(0, 55)}
                                    {item.dueAt && ` — by ${new Date(item.dueAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-stone-400 italic">No approvals needed today.</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* What We Handled */}
                    <div>
                      <h4 className="text-sm font-medium text-stone-700 mb-2">What We Handled</h4>
                      <p className="text-xs text-stone-500 mb-3">The work that got closed — organized by priority.</p>
                      {briefingData.whatWeHandled.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-stone-100">
                                <th className="text-left py-2 px-2 font-medium text-stone-500 text-xs">Priority</th>
                                <th className="text-left py-2 px-2 font-medium text-stone-500 text-xs">Property</th>
                                <th className="text-left py-2 px-2 font-medium text-stone-500 text-xs">Category</th>
                                <th className="text-left py-2 px-2 font-medium text-stone-500 text-xs">Summary</th>
                                <th className="text-left py-2 px-2 font-medium text-stone-500 text-xs">Closed</th>
                                <th className="text-left py-2 px-2 font-medium text-stone-500 text-xs">Owner</th>
                              </tr>
                            </thead>
                            <tbody>
                              {briefingData.whatWeHandled.map((item, idx) => (
                                <tr key={idx} className="border-b border-stone-50">
                                  <td className="py-2 px-2">
                                    <Badge variant="outline" className={`text-[10px] ${
                                      item.priority === 'Handle Now' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-amber-50 text-amber-600 border-amber-200'
                                    }`}>
                                      {item.priority}
                                    </Badge>
                                  </td>
                                  <td className="py-2 px-2">
                                    <PropertyBadge propertyId={item.propertyId} variant="inline" size="sm" />
                                  </td>
                                  <td className="py-2 px-2 text-stone-600 text-xs">{item.category}</td>
                                  <td className="py-2 px-2 text-stone-700 text-xs">{item.summary}</td>
                                  <td className="py-2 px-2 text-stone-500 text-xs">{item.closed}</td>
                                  <td className="py-2 px-2">
                                    <Badge variant="secondary" className="text-[10px]">{item.owner}</Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-stone-50 rounded-lg">
                          <CheckCircle className="h-6 w-6 text-stone-300 mx-auto mb-2" />
                          <p className="text-sm text-stone-600 font-medium">Nothing marked complete yet</p>
                          <p className="text-xs text-stone-400">As items are closed in Daily Run, they'll appear here automatically.</p>
                        </div>
                      )}
                    </div>

                    {/* What's Still Waiting */}
                    <div>
                      <h4 className="text-sm font-medium text-stone-700 mb-2">What's Still Waiting</h4>
                      <p className="text-xs text-stone-500 mb-3">Open items that need a next touch — no drifting.</p>
                      {briefingData.stillWaiting.length > 0 ? (
                        <div className="space-y-2">
                          {briefingData.stillWaiting.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                              <div className="flex items-center gap-3 flex-1">
                                <Badge variant="outline" className={`text-[10px] ${
                                  item.priority === 'Now' ? 'bg-red-50 text-red-600' : 
                                  item.priority === 'Today' ? 'bg-amber-50 text-amber-600' : 'bg-indigo-50 text-indigo-600'
                                }`}>
                                  {item.priority}
                                </Badge>
                                <PropertyBadge propertyId={item.propertyId} variant="inline" size="sm" />
                                <Badge variant="secondary" className="text-[10px]">Waiting on {item.waitingOn}</Badge>
                                <span className="text-xs text-stone-600">{item.summary}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-stone-500">Next: {item.nextTouch}</span>
                                {item.owner ? (
                                  <Badge variant="outline" className="text-[10px]">{item.owner}</Badge>
                                ) : (
                                  <Button variant="ghost" size="sm" className="text-[10px] h-6 text-stone-500">
                                    <UserPlus className="h-3 w-3 mr-1" /> Assign
                                  </Button>
                                )}
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                    <Bell className="h-3 w-3 text-stone-400" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                    <Send className="h-3 w-3 text-stone-400" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-stone-50 rounded-lg">
                          <CheckCircle className="h-6 w-6 text-green-400 mx-auto mb-2" />
                          <p className="text-sm text-stone-600 font-medium">All clear</p>
                          <p className="text-xs text-stone-400">Nothing is waiting right now. If something comes in, it will land in Daily Run.</p>
                        </div>
                      )}
                    </div>

                    {/* Carry-Over Notes */}
                    {briefingData.carryOverNotes.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-stone-700 mb-2">Carry-Over Notes</h4>
                        <p className="text-xs text-stone-500 mb-3">Anything not finished gets a reason and a next touch time.</p>
                        <div className="space-y-2">
                          {briefingData.carryOverNotes.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                              <div className="flex items-center gap-3 flex-1">
                                <PropertyBadge propertyId={item.propertyId} variant="inline" size="sm" />
                                <span className="text-xs text-stone-700">{item.summary}</span>
                                <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">
                                  {item.reason}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-stone-500">Next: {item.nextTouch}</span>
                                {item.owner ? (
                                  <Badge variant="outline" className="text-[10px]">{item.owner}</Badge>
                                ) : (
                                  <Button variant="ghost" size="sm" className="text-[10px] h-6 text-stone-500">
                                    <UserPlus className="h-3 w-3 mr-1" /> Assign
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Guest Pressure Points */}
                    <div>
                      <h4 className="text-sm font-medium text-stone-700 mb-2">Guest Pressure Points</h4>
                      <p className="text-xs text-stone-500 mb-3">The themes that created the most friction — so we can prevent repeats.</p>
                      {briefingData.pressurePoints.length > 0 ? (
                        <div className="space-y-2">
                          {briefingData.pressurePoints.map((point, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <AlertTriangle className="h-4 w-4 text-amber-500" />
                                <span className="text-sm font-medium text-stone-700">{point.category}</span>
                                <Badge variant="secondary" className="text-[10px]">{point.count} requests</Badge>
                                <span className="text-xs text-stone-500">{point.properties} properties</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-stone-600 italic">"{point.fix}"</span>
                                <Button variant="ghost" size="sm" className="text-[10px] h-6" onClick={() => {
                                  navigator.clipboard.writeText(point.fix);
                                  toast.success('Fix copied');
                                }}>
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-stone-50 rounded-lg">
                          <Shield className="h-6 w-6 text-green-400 mx-auto mb-2" />
                          <p className="text-sm text-stone-600 font-medium">No repeat friction today</p>
                          <p className="text-xs text-stone-400">Nothing repeated enough to flag. If patterns show up, they'll appear here.</p>
                        </div>
                      )}
                    </div>

                    {/* Tomorrow's Head Start */}
                    <div>
                      <h4 className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                        <Sunrise className="h-4 w-4 text-amber-500" />
                        Tomorrow's Head Start
                      </h4>
                      <p className="text-xs text-stone-500 mb-3">The 3 things to tee up early, based on what's still open and what's due.</p>
                      {briefingData.tomorrowHeadStart.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {briefingData.tomorrowHeadStart.map((item, idx) => (
                            <div key={idx} className={`p-3 rounded-lg border ${
                              item.type === 'first_touch' ? 'bg-blue-50 border-blue-100' :
                              item.type === 'prevent_repeat' ? 'bg-amber-50 border-amber-100' :
                              'bg-green-50 border-green-100'
                            }`}>
                              <p className={`text-[10px] font-medium mb-1 ${
                                item.type === 'first_touch' ? 'text-blue-600' :
                                item.type === 'prevent_repeat' ? 'text-amber-600' :
                                'text-green-600'
                              }`}>
                                {item.type === 'first_touch' ? 'First touch' :
                                 item.type === 'prevent_repeat' ? 'Prevent a repeat' :
                                 'Protect bookings'}
                              </p>
                              <p className="text-xs text-stone-700">{item.text}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-stone-50 rounded-lg">
                          <Coffee className="h-6 w-6 text-stone-300 mx-auto mb-2" />
                          <p className="text-sm text-stone-600 font-medium">Tomorrow is set up nicely</p>
                          <p className="text-xs text-stone-400">No high-risk items scheduled yet.</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-10">
                    <ClipboardList className="h-10 w-10 text-stone-300 mx-auto mb-3" />
                    <p className="text-stone-600 font-medium">No shift activity logged yet</p>
                    <p className="text-sm text-stone-400 mt-1">Once tasks are handled through Daily Run, this briefing will write itself.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 2) Conversion Funnel */}
            <Card className="border-stone-200">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg">Conversion Funnel</CardTitle>
                <CardDescription>
                  From inquiry to outcome — so you can see where things stall.
                  <span className="block text-xs mt-1">Open = not booked and not marked lost yet.</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                {report.totalInquiries > 0 ? (
                  <>
                    <div className="space-y-3">
                      {/* Inquiries */}
                      <div className="flex items-center gap-4">
                        <div className="w-36 text-sm text-stone-600">Inquiries</div>
                        <div className="flex-1 bg-stone-100 rounded-full h-7 overflow-hidden">
                          <div 
                            className="h-full bg-[#3F617E] rounded-full flex items-center justify-end pr-3"
                            style={{ width: '100%' }}
                          >
                            <span className="text-white text-xs font-medium">{report.totalInquiries}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Open */}
                      <div className="flex items-center gap-4">
                        <div className="w-36 text-sm text-stone-600">Open</div>
                        <div className="flex-1 bg-stone-100 rounded-full h-7 overflow-hidden">
                          <div 
                            className="h-full bg-amber-500 rounded-full flex items-center justify-end pr-3"
                            style={{ width: `${Math.max(5, (openCount / report.totalInquiries) * 100)}%` }}
                          >
                            <span className="text-white text-xs font-medium">{openCount}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Booked */}
                      <div className="flex items-center gap-4">
                        <div className="w-36 text-sm text-stone-600">Booked</div>
                        <div className="flex-1 bg-stone-100 rounded-full h-7 overflow-hidden">
                          <div 
                            className="h-full bg-green-500 rounded-full flex items-center justify-end pr-3"
                            style={{ width: `${Math.max(5, ((report.booked || 0) / report.totalInquiries) * 100)}%` }}
                          >
                            <span className="text-white text-xs font-medium">{report.booked || 0}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Lost */}
                      <div className="flex items-center gap-4">
                        <div className="w-36 text-sm text-stone-600">Lost</div>
                        <div className="flex-1 bg-stone-100 rounded-full h-7 overflow-hidden">
                          <div 
                            className="h-full bg-red-400 rounded-full flex items-center justify-end pr-3"
                            style={{ width: `${Math.max(5, ((report.lost || 0) / report.totalInquiries) * 100)}%` }}
                          >
                            <span className="text-white text-xs font-medium">{report.lost || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-stone-400 mt-4">
                      Tip: A high <span className="text-amber-600 font-medium">Open</span> count usually means a few follow-ups are still pending.
                    </p>
                  </>
                ) : (
                  <EmptyState 
                    icon={TrendingUp}
                    title="No inquiry flow yet"
                    description="When inquiries start, you'll see where they're landing: Open, Booked, or Lost—so nothing quietly stalls."
                    hint="'Open' usually means someone needs a follow-up."
                  />
                )}
              </CardContent>
            </Card>

            {/* 3) Service Health */}
            <Card className="border-stone-200">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="font-heading text-lg">Service Health</CardTitle>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-stone-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Times are based on message timestamps in this date range.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <CardDescription>
                  Speed matters — especially when guests are anxious or mid-stay.
                  <span className="block text-xs mt-1 italic">This is about response rhythm — not perfection.</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                {report.avgResponseTime || hasEnoughData ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* First Response Time */}
                    <div className="p-4 bg-stone-50 rounded-xl">
                      <p className="text-sm font-medium text-stone-600 mb-2">First Response Time</p>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-stone-500">Median</span>
                          <span className="font-medium">{formatResponseTime(report.avgResponseTime)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-stone-500">Avg</span>
                          <span className="font-medium">{formatResponseTime(report.avgResponseTime ? report.avgResponseTime * 1.5 : null)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-stone-500">Fastest</span>
                          <span className="font-medium text-green-600">2 min</span>
                        </div>
                      </div>
                      <p className="text-xs text-stone-400 mt-2">How quickly guests hear back the first time.</p>
                    </div>
                    
                    {/* Resolution Time */}
                    <div className="p-4 bg-stone-50 rounded-xl">
                      <p className="text-sm font-medium text-stone-600 mb-2">Time to Resolve</p>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-stone-500">Median</span>
                          <span className="font-medium">2h 10m</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-stone-500">Avg</span>
                          <span className="font-medium">4h 05m</span>
                        </div>
                      </div>
                      <p className="text-xs text-stone-400 mt-2">From first message to "sorted".</p>
                    </div>
                    
                    {/* On-Time Replies */}
                    <div className="p-4 bg-stone-50 rounded-xl">
                      <p className="text-sm font-medium text-stone-600 mb-2">On-Time Replies</p>
                      <p className="text-3xl font-heading font-medium text-[#3F617E]">{report.pctWithinHour || 0}%</p>
                      <p className="text-xs text-stone-400 mt-2">
                        Urgent items should be answered fast — even if the fix takes longer.
                      </p>
                      <div className="mt-2 text-xs text-stone-500">
                        <div>Urgent target: 15 min</div>
                        <div>Standard target: 60 min</div>
                      </div>
                    </div>
                  </div>
                ) : hasData && !hasEnoughData ? (
                  <div className="p-6 bg-stone-50 rounded-xl text-center">
                    <Timer className="h-8 w-8 text-stone-400 mx-auto mb-2" />
                    <p className="text-stone-600 font-medium">Not enough history yet</p>
                    <p className="text-sm text-stone-500 mt-1">
                      Give it a little time. Once we have a few replies logged, we'll show response rhythm: first reply, time to resolve, and on-time coverage.
                    </p>
                    <p className="text-xs text-stone-400 mt-2">This is about consistency—not perfection.</p>
                  </div>
                ) : (
                  <EmptyState 
                    icon={Timer}
                    title="Not enough history yet"
                    description="Give it a little time. Once we have a few replies logged, we'll show response rhythm: first reply, time to resolve, and on-time coverage."
                    hint="This is about consistency—not perfection."
                  />
                )}
              </CardContent>
            </Card>

            {/* 4) Most Common Requests */}
            <Card className="border-stone-200">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="font-heading text-lg">Most Common Requests</CardTitle>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-stone-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Based on tagged request types and repeated topics.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <CardDescription>This is what guests are asking for — so you can standardize, prep, or fix.</CardDescription>
              </CardHeader>
              <CardContent>
                {commonRequests.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-stone-100">
                            <th className="text-left py-2 px-2 font-medium text-stone-500">Category</th>
                            <th className="text-right py-2 px-2 font-medium text-stone-500">Count</th>
                            <th className="text-right py-2 px-2 font-medium text-stone-500">% of total</th>
                            <th className="text-right py-2 px-2 font-medium text-stone-500">Trend</th>
                          </tr>
                        </thead>
                        <tbody>
                          {commonRequests.map((req, idx) => (
                            <tr key={idx} className="border-b border-stone-50 hover:bg-stone-50">
                              <td className="py-2.5 px-2 text-stone-700">{req.category}</td>
                              <td className="py-2.5 px-2 text-right font-medium">{req.count}</td>
                              <td className="py-2.5 px-2 text-right text-stone-500">{req.pct}%</td>
                              <td className="py-2.5 px-2 text-right">
                                <TrendBadge trend={req.trend} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-stone-400 mt-3">
                      If a request shows up constantly, it belongs in GuestHub or a saved reply.
                    </p>
                  </>
                ) : (
                  <EmptyState 
                    icon={HelpCircle}
                    title="No patterns yet"
                    description="When requests repeat, you'll see them here—so you can prep smarter (and stop answering the same thing 20 times)."
                    hint="Examples: Wi-Fi, check-in help, taxis, late checkout."
                  />
                )}
              </CardContent>
            </Card>

            {/* 5) Recurring Issues */}
            <Card className="border-stone-200">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg">Repeats & Friction</CardTitle>
                <CardDescription>These repeat problems are the ones that quietly damage reviews.</CardDescription>
              </CardHeader>
              <CardContent>
                {recurringIssues.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-stone-100">
                          <th className="text-left py-2 px-2 font-medium text-stone-500">Issue</th>
                          <th className="text-right py-2 px-2 font-medium text-stone-500">Count</th>
                          <th className="text-left py-2 px-2 font-medium text-stone-500">Property</th>
                          <th className="text-left py-2 px-2 font-medium text-stone-500">Last seen</th>
                          <th className="text-left py-2 px-2 font-medium text-stone-500">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recurringIssues.map((issue, idx) => (
                          <tr key={idx} className="border-b border-stone-50 hover:bg-stone-50">
                            <td className="py-2.5 px-2">
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4 text-amber-500" />
                                <span className="text-stone-700">{issue.issue}</span>
                              </div>
                            </td>
                            <td className="py-2.5 px-2 text-right font-medium">{issue.count}</td>
                            <td className="py-2.5 px-2 text-stone-500">{issue.properties}</td>
                            <td className="py-2.5 px-2 text-stone-500">{issue.lastSeen}</td>
                            <td className="py-2.5 px-2">
                              <Badge variant="outline" className={`text-xs ${
                                issue.status === 'Open' ? 'text-red-600 border-red-200' : 
                                issue.status === 'Recurring' ? 'text-amber-600 border-amber-200' : 
                                'text-green-600 border-green-200'
                              }`}>
                                {issue.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState 
                    icon={Shield}
                    title="Quiet week — good sign"
                    description="Nothing is repeating enough to flag. If something starts popping up (hot water, A/C, lock issues), this section will catch it early."
                    hint="Repeats are usually a process issue, not a people issue."
                  />
                )}
              </CardContent>
            </Card>

            {/* 6) After-Hours Activity */}
            <Card className="border-stone-200">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg">After-Hours Activity</CardTitle>
                <CardDescription>Guests don't always message during office hours. This shows what's coming in late.</CardDescription>
              </CardHeader>
              <CardContent>
                {hasData ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-stone-50 rounded-xl text-center">
                        <Moon className="h-6 w-6 text-indigo-500 mx-auto mb-2" />
                        <p className="text-2xl font-heading font-medium">8</p>
                        <p className="text-xs text-stone-500">After-hours messages</p>
                      </div>
                      <div className="p-4 bg-stone-50 rounded-xl text-center">
                        <HelpCircle className="h-6 w-6 text-amber-500 mx-auto mb-2" />
                        <p className="text-sm font-medium text-stone-700">WiFi / Access</p>
                        <p className="text-xs text-stone-500">Most common after-hours</p>
                      </div>
                      <div className="p-4 bg-stone-50 rounded-xl text-center">
                        <BellRing className="h-6 w-6 text-green-500 mx-auto mb-2" />
                        <p className="text-2xl font-heading font-medium">6</p>
                        <p className="text-xs text-stone-500">Auto-ack sent</p>
                      </div>
                    </div>
                    <p className="text-xs text-stone-400 mt-3">
                      If after-hours requests are predictable, automation + clear GuestHub info can reduce stress.
                    </p>
                  </>
                ) : (
                  <EmptyState 
                    icon={Moon}
                    title="Coverage isn't set yet"
                    description="Add office hours to see what's coming in after-hours—and what should get an auto-reply vs. a wake-someone-up alert."
                    hint="Guests don't mind waiting when they know what to expect."
                  />
                )}
              </CardContent>
            </Card>

            {/* 7) Reviews & Reputation */}
            <Card className="border-stone-200">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg">Reviews & Reputation</CardTitle>
                <CardDescription>Reviews aren't just scores — they're a summary of your operations.</CardDescription>
              </CardHeader>
              <CardContent>
                {report.reviewsReceived > 0 ? (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="text-center p-3 bg-stone-50 rounded-lg">
                        <p className="text-2xl font-heading font-medium">{report.reviewsReceived || 0}</p>
                        <p className="text-xs text-stone-500">Received</p>
                      </div>
                      <div className="text-center p-3 bg-stone-50 rounded-lg">
                        <p className="text-2xl font-heading font-medium">{report.avgRating ? report.avgRating.toFixed(1) : '—'}</p>
                        <p className="text-xs text-stone-500">Avg Rating</p>
                      </div>
                      <div className="text-center p-3 bg-stone-50 rounded-lg">
                        <p className="text-2xl font-heading font-medium text-amber-600">{report.pendingReviews || 0}</p>
                        <p className="text-xs text-stone-500">Pending Response</p>
                      </div>
                      <div className="text-center p-3 bg-stone-50 rounded-lg">
                        <p className="text-2xl font-heading font-medium text-green-600">{responseRate}%</p>
                        <p className="text-xs text-stone-500">Response Rate</p>
                      </div>
                    </div>
                    <p className="text-xs text-stone-400">
                      A quick response protects your reputation — even when the guest isn't happy.
                    </p>
                  </>
                ) : (
                  <EmptyState 
                    icon={Star}
                    title="No reviews logged yet"
                    description="When reviews come in, you'll see ratings, response rate, and what guests keep praising (or side-eyeing)."
                    hint="A simple, timely reply goes a long way."
                  />
                )}
              </CardContent>
            </Card>

            {/* 8) What to Fix Next */}
            <Card className="border-stone-200 border-2 border-[#3F617E]/20">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-amber-500" />
                  What to Fix Next
                </CardTitle>
                <CardDescription>A short, practical list based on this period's patterns.</CardDescription>
              </CardHeader>
              <CardContent>
                {fixSuggestions.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {fixSuggestions.map((fix, idx) => (
                      <div key={idx} className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                        <p className="font-medium text-stone-800">{fix.title}</p>
                        <p className="text-xs text-amber-700 mt-1">Why: {fix.why}</p>
                        <p className="text-sm text-stone-600 mt-2">{fix.action}</p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-3 text-xs h-7"
                          onClick={() => {
                            navigator.clipboard.writeText(fix.action);
                            toast.success('Suggestion copied');
                          }}
                        >
                          <Copy className="h-3 w-3 mr-1" /> Copy suggestion
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState 
                    icon={Lightbulb}
                    title="Nothing flagged right now"
                    description="When Maris Desk spots a pattern—slow replies, repeated issues, or open inquiries—it'll list the clearest 'fix next' items here."
                    hint="Calm is the goal. This section only shows what matters."
                  />
                )}
              </CardContent>
            </Card>
          </>
        )}
        
        {/* Email Briefing Modal */}
        <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Mail className="h-5 w-5 text-[#3F617E]" />
                Send Concierge Briefing
              </DialogTitle>
              <DialogDescription>
                Send a summary email to your manager or property owner.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Recipient Email *</Label>
                <Input 
                  type="email"
                  value={emailRecipient} 
                  onChange={(e) => setEmailRecipient(e.target.value)}
                  placeholder="manager@property.com"
                  data-testid="email-recipient-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Recipient Name</Label>
                <Input 
                  value={emailRecipientName} 
                  onChange={(e) => setEmailRecipientName(e.target.value)}
                  placeholder="Property Manager"
                />
              </div>
              <div className="space-y-2">
                <Label>Report Period</Label>
                <Select value={emailPeriod} onValueChange={setEmailPeriod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="yesterday">Yesterday</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="pt-2 flex items-center justify-between text-xs text-stone-500 bg-stone-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  <span>Demo mode: Email will be logged and previewed</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-xs h-7"
                  onClick={loadEmailHistory}
                >
                  <History className="h-3 w-3 mr-1" /> View History
                </Button>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowEmailModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendBriefing}
                disabled={sendingEmail || !emailRecipient}
                className="bg-[#3F617E] hover:bg-[#345470]"
                data-testid="send-email-button"
              >
                {sendingEmail ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send Briefing
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Email Preview Modal */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Eye className="h-5 w-5 text-[#3F617E]" />
                Email Preview
              </DialogTitle>
              <DialogDescription>
                This is how the briefing email will appear to the recipient.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 border border-stone-200 rounded-lg overflow-auto max-h-[60vh]">
              {emailPreview && (
                <iframe 
                  srcDoc={emailPreview}
                  title="Email Preview"
                  className="w-full h-[500px] border-0"
                  sandbox="allow-same-origin"
                />
              )}
            </div>
            <div className="flex justify-between items-center pt-4">
              <Badge variant="outline" className="text-green-600 border-green-200">
                <CheckCircle className="h-3 w-3 mr-1" /> Email Logged Successfully
              </Badge>
              <Button onClick={() => setShowPreview(false)}>
                Close Preview
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Email History Modal */}
        <Dialog open={showEmailHistory} onOpenChange={setShowEmailHistory}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <History className="h-5 w-5 text-[#3F617E]" />
                Email History
              </DialogTitle>
              <DialogDescription>
                Previously sent briefing emails (demo mode).
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 max-h-[400px] overflow-auto">
              {emailLogs.length > 0 ? (
                <div className="space-y-3">
                  {emailLogs.map((log, idx) => (
                    <div key={idx} className="p-3 bg-stone-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-stone-800 text-sm">{log.subject}</p>
                        <Badge variant="outline" className="text-xs text-green-600 border-green-200">
                          {log.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-stone-500 mt-1">
                        To: {log.recipientEmail}
                        {log.recipientName && ` (${log.recipientName})`}
                      </p>
                      <p className="text-xs text-stone-400 mt-0.5">
                        {new Date(log.sentAt).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Mail className="h-10 w-10 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">No emails sent yet</p>
                  <p className="text-xs text-stone-400 mt-1">Send your first briefing to see it here.</p>
                </div>
              )}
            </div>
            <div className="flex justify-end pt-4">
              <Button variant="outline" onClick={() => setShowEmailHistory(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DeskLayout>
  );
};

export default Reports;
